import React, { useState, useMemo } from 'react';
import { Skeleton } from '@mui/material';
import { getCountryByCode } from 'constants/countries';
import { getAvatarPlaceholder } from 'utils/avatarUtils';
import './AvatarWithFlag.css';

interface AvatarWithFlagProps {
  avatarUrl?: string | null;
  username?: string;
  size?: number;
  countryCode?: string | null;
  className?: string;
  isLoading?: boolean;
  isElite?: boolean;
  showAnimatedBorder?: boolean;
  borderColor1?: string | null;
  borderColor2?: string | null;
}

// Get flag size based on avatar size (45% of avatar, clamped 14-29px)
const getFlagSize = (avatarSize: number): number => {
  const calculated = Math.round(avatarSize * 0.45);
  return Math.max(14, Math.min(29, calculated));
};

// Get animated border width based on avatar size
const getBorderWidth = (avatarSize: number): number => {
  if (avatarSize > 80) return 4;
  if (avatarSize > 32) return 3;
  return 2;
};

// Common alpha-3 to alpha-2 conversions
const ALPHA3_TO_ALPHA2: Record<string, string> = {
  USA: 'US',
  GBR: 'GB',
  CAN: 'CA',
  AUS: 'AU',
  DEU: 'DE',
  FRA: 'FR',
  JPN: 'JP',
  MEX: 'MX',
  BRA: 'BR',
  ITA: 'IT',
  ESP: 'ES',
  NLD: 'NL',
  CHN: 'CN',
  IND: 'IN',
  RUS: 'RU',
  KOR: 'KR',
  ARG: 'AR',
  POL: 'PL',
  SWE: 'SE',
  NOR: 'NO',
  DNK: 'DK',
  FIN: 'FI',
  CHE: 'CH',
  AUT: 'AT',
  BEL: 'BE',
  PRT: 'PT',
  IRL: 'IE',
  NZL: 'NZ',
  SGP: 'SG',
  HKG: 'HK',
  TWN: 'TW',
  PHL: 'PH',
  THA: 'TH',
  MYS: 'MY',
  IDN: 'ID',
  VNM: 'VN',
  ZAF: 'ZA',
  ISR: 'IL',
  ARE: 'AE',
  SAU: 'SA',
  TUR: 'TR',
  GRC: 'GR',
  CZE: 'CZ',
  HUN: 'HU',
  ROU: 'RO',
  UKR: 'UA',
  COL: 'CO',
  CHL: 'CL',
  PER: 'PE',
};

// Normalize country code to alpha-2 format
const normalizeCountryCode = (code: string): string => {
  const upper = code.toUpperCase().trim();
  // If it's 3 letters, try to convert from alpha-3
  if (upper.length === 3) {
    return ALPHA3_TO_ALPHA2[upper] || upper.substring(0, 2);
  }
  // Already alpha-2 or shorter
  return upper;
};

export const AvatarWithFlag: React.FC<AvatarWithFlagProps> = ({
  avatarUrl,
  username,
  size = 32,
  countryCode,
  className,
  isLoading,
  isElite,
  showAnimatedBorder,
  borderColor1,
  borderColor2,
}) => {
  const [imageError, setImageError] = useState(false);

  const avatarPlaceholder = useMemo(
    () => getAvatarPlaceholder(username),
    [username]
  );

  const normalizedCode = countryCode ? normalizeCountryCode(countryCode) : null;

  const country = useMemo(
    () => (normalizedCode ? getCountryByCode(normalizedCode) : undefined),
    [normalizedCode]
  );

  const flagSize = getFlagSize(size);
  const hasAnimatedBorder = isElite && showAnimatedBorder;
  const borderWidth = hasAnimatedBorder ? getBorderWidth(size) : 0;

  if (isLoading) {
    return (
      <Skeleton
        variant="circular"
        width={size}
        height={size}
        sx={{ backgroundColor: 'var(--darker-grey)' }}
      />
    );
  }

  // Check if we should show placeholder instead of image
  const shouldShowPlaceholder =
    !avatarUrl ||
    avatarUrl === '' ||
    avatarUrl === 'undefined' ||
    avatarUrl === 'null' ||
    imageError;

  return (
    <div
      className={`avatar-with-flag ${hasAnimatedBorder ? 'avatar-with-flag--animated-border' : ''} ${className || ''}`}
      style={{
        width: size,
        height: size,
        '--avatar-border-width': `${borderWidth}px`,
        '--border-color-1': borderColor1 || '#FFD700',
        '--border-color-2': borderColor2 || '#9B59B6',
      } as React.CSSProperties}
    >
      {/* Avatar Image or Placeholder */}
      {!shouldShowPlaceholder ? (
        <img
          src={avatarUrl!}
          alt={username || 'User avatar'}
          className="avatar-with-flag__image"
          onError={() => setImageError(true)}
        />
      ) : avatarPlaceholder ? (
        <div
          className="avatar-with-flag__placeholder"
          style={{
            backgroundColor: avatarPlaceholder.color,
            fontSize: size * 0.4,
          }}
        >
          {avatarPlaceholder.letter}
        </div>
      ) : (
        <div
          className="avatar-with-flag__placeholder"
          style={{ backgroundColor: '#8409D3', fontSize: size * 0.4 }}
        >
          ?
        </div>
      )}

      {/* Country Flag Badge - Using image URL instead of React component */}
      {countryCode && normalizedCode && (
        <div
          style={{
            position: 'absolute',
            bottom: -4,
            right: -4,
            width: flagSize,
            height: flagSize,
            zIndex: 9999,
            borderRadius: '50%',
            border: '2px solid #000',
            backgroundColor: '#fff',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          title={country?.name}
        >
          <img
            src={`https://purecatamphetamine.github.io/country-flag-icons/3x2/${normalizedCode}.svg`}
            alt={country?.name || normalizedCode}
            style={{
              width: '140%',
              height: '140%',
              objectFit: 'cover',
            }}
            onError={(e) => {
              // Hide on error
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        </div>
      )}
    </div>
  );
};

export default AvatarWithFlag;
